import { ElementRef, Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Injectable()
export class ResizeObserverService implements OnDestroy {
    private resizeObserver: ResizeObserver;
    private observedElements = new WeakMap<Element, { subject: Subject<ResizeObserverEntry>; subscription: Subscription }>();

    constructor() {
        this.resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const data = this.observedElements.get(entry.target);
                data?.subject.next(entry);
            }
        });
    }

    public observe(element: ElementRef | HTMLElement, callback: (entry: ResizeObserverEntry) => void, debounceMs: number = 50) {
        const nativeElement = element instanceof ElementRef ? element.nativeElement : element;

        if (!nativeElement) {
            return;
        }

        const data = this.observedElements.get(nativeElement);
        if (data) {
            this.unsubscribeObservedElement(data.subscription, data.subject);
        }

        const subject = new Subject<ResizeObserverEntry>();
        const subscription = subject.pipe(debounceTime(debounceMs)).subscribe(callback);

        this.observedElements.set(nativeElement, { subject, subscription });

        this.resizeObserver.observe(nativeElement);
    }

    public unobserve(element: ElementRef | HTMLElement) {
        const nativeElement = element instanceof ElementRef ? element?.nativeElement : element;
        const data = this.observedElements.get(nativeElement);
        if (data) {
            this.unsubscribeObservedElement(data.subscription, data.subject);
            this.observedElements.delete(nativeElement);
        }

        this.resizeObserver.unobserve(nativeElement);
    }

    public ngOnDestroy() {
        this.resizeObserver.disconnect();
    }

    private unsubscribeObservedElement(subscription: Subscription, subject: Subject<ResizeObserverEntry>) {
        subscription.unsubscribe();
        subject.complete();
    }
}
